import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaDownload, FaTrash, FaRedo, FaSync, FaSpinner } from 'react-icons/fa'
import moment from 'moment'
import CustomTable from './CustomTable'
import { formatDate } from '../utils/FormateDateTime'

const DOWNLOAD_WINDOW_MINUTES = 10
const REFRESH_INTERVAL_MS = 2 * 60 * 1000

const ReportJobsList = ({ jobs, onDownload, onRemove, onRetry, downloadingJobs = {}, reportTitle = 'Report' }) => {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => moment())

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(moment())
    }, REFRESH_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [])

  if (!jobs || jobs.length === 0) {
    return null
  }

  const isActive = (job) => ['queued', 'processing'].includes(job.status)
  const isNoData = (job) => job.status === 'completed' && job.noData
  const canDownload = (job) => {
    if (job.status !== 'completed' || isNoData(job) || !job.eta) {
      return false
    }

    const expiresAt = moment(job.eta).add(DOWNLOAD_WINDOW_MINUTES, 'minutes')
    return now.isSameOrBefore(expiresAt)
  }

  const columns = [
    {
      header: t('Report'),
      body: (job) => (
        <div className='text-xs font-medium text-gray-700'>
          {reportTitle}
          <span className='ml-2 text-gray-400 uppercase'>{job.format}</span>
        </div>
      ),
      style: { width: '18%' }
    },
    {
      header: t('Start Date'),
      body: (job) => (
        <div className='text-xs text-gray-600'>
          {job.filters?.start_date ? formatDate(job.filters.start_date) : '—'}
        </div>
      ),
      style: { width: '12%' }
    },
    {
      header: t('End Date'),
      body: (job) => (
        <div className='text-xs text-gray-600'>
          {job.filters?.end_date ? formatDate(job.filters.end_date) : '—'}
        </div>
      ),
      style: { width: '12%' }
    },
    {
      header: t('Progress'),
      body: (job) => (
        <div className='flex items-center gap-2'>
          <div className='w-[100px] bg-gray-200 h-1.5 rounded-full overflow-hidden'>
            <div
              className='bg-primary h-full rounded-full transition-all duration-700'
              style={{ width: `${Math.max(job.status === 'completed' ? 100 : job.progress || 0, 2)}%` }}
            />
          </div>
          <span className='text-xs text-gray-500'>{Math.round(job.status === 'completed' ? 100 : job.progress || 0)}%</span>
        </div>
      ),
      style: { width: '15%' }
    },
    {
      header: t('Status'),
      body: (job) => (
        <div>
          {isActive(job) && (
            <span className='flex items-center gap-1 text-primary font-medium capitalize'>
              <FaSync className='animate-spin text-[12px]' /> {job.status}
            </span>
          )}
          {job.status === 'completed' && !isNoData(job) && <span className='text-green-600 font-semibold'>✓ {t('Ready')}</span>}
          {isNoData(job) && <span className='text-orange-500 font-semibold'>{t('No Data')}</span>}
          {job.status === 'failed' && <span className='text-red-600 font-semibold'>✕ {t('Failed')}</span>}
        </div>
      ),
      style: { width: '10%' }
    },
    {
      header: t('Records'),
      body: (job) => (
        job.totalRecords > 0
          ? `${job.processedCount?.toLocaleString()} / ${job.totalRecords?.toLocaleString()}`
          : '—'
      ),
      style: { width: '11%' }
    },
    {
      header: t('ETA'),
      body: (job) => (
        job.eta ? moment(job.eta).format('HH:mm') : '—'
      ),
      style: { width: '7%' }
    },
    {
      header: t('Actions'),
      body: (job) => (
        <div>
          {job.status === 'completed' && (
            <div className='flex items-center gap-3'>
              {isNoData(job) ? (
                <span className='text-xs text-gray-400 italic'>{t('No Data')}</span>
              ) : canDownload(job) && downloadingJobs[job.jobId] ? (
                <FaSpinner className='animate-spin text-primary text-sm' />
              ) : canDownload(job) ? (
                <FaDownload
                  title={t('Download')}
                  onClick={() => onDownload(job)}
                  className='text-primary cursor-pointer hover:opacity-70 text-sm'
                />
              ) : (
                <span className='text-xs text-gray-400 italic'>{t('Expired')}</span>
              )}
              <FaTrash
                title={t('Remove')}
                onClick={() => onRemove(job.jobId)}
                className='text-gray-400 cursor-pointer hover:text-red-600 text-sm'
              />
            </div>
          )}
          {job.status === 'failed' && (
            <div className='flex items-center gap-3'>
              <FaRedo
                title={t('Retry')}
                onClick={() => onRetry(job)}
                className='text-primary cursor-pointer hover:opacity-70 text-sm'
              />
              <FaTrash
                title={t('Remove')}
                onClick={() => onRemove(job.jobId)}
                className='text-gray-400 cursor-pointer hover:text-red-600 text-sm'
              />
              {job.error && <span className='text-xs text-red-400 truncate max-w-[200px]'>{job.error}</span>}
            </div>
          )}
          {isActive(job) && (
            <div className='flex items-center gap-3'>
              <span className='text-xs text-gray-400 italic'>{t('In progress...')}</span>
              <FaTrash
                title={t('Remove')}
                onClick={() => onRemove(job.jobId)}
                className='text-gray-400 cursor-pointer hover:text-red-600 text-sm'
              />
            </div>
          )}
        </div>
      ),
      style: { width: '15%' }
    }
  ]

  return (
    <div className='mt-2'>
      <CustomTable
        data={jobs}
        columns={columns}
        emptyMessage='No report jobs found'
      //tableStyle={{ minWidth: '100%' }}
      />
    </div>
  )
}

export default ReportJobsList
